import { useState } from "react"
import { Layout } from "../components/Layout"
import { useObservability } from "@/hooks/useObservability"
import { CardSkeleton } from "@/components/Skeleton/Skeleton"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  BoltIcon,
  ClockIcon,
} from "@heroicons/react/24/outline"

const WINDOWS = [
  { label: "24h", minutes: 1440 },
  { label: "7d", minutes: 7 * 24 * 60 },
]

function fmt(n: number): string {
  // Coerce first: the API serializes AE/PostHog numeric columns as strings, and an absent
  // metric (e.g. p95 on an empty bucket) as null — a bare Math.round would render "NaN".
  return (Math.round(Number(n)) || 0).toLocaleString("en-US")
}

// A section that's unavailable (source query failed) — shown instead of empty/data.
function Unavailable({ what, error }: { what: string; error?: string }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-300">
      <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
      <span>
        {what} unavailable{error ? `: ${error}` : ""}
      </span>
    </div>
  )
}

function AllClear({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-emerald-400">
      <CheckCircleIcon className="h-5 w-5" />
      <span>{label}</span>
    </div>
  )
}

export function Observability() {
  const [windowMinutes, setWindowMinutes] = useState(1440)
  const { data, isLoading, error } = useObservability(windowMinutes)

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-primary">Observability</h1>
            <p className="text-secondary">
              5xx, sustained 4xx, latency &amp; JS exceptions
              {data ? ` · ${data.environment} · updated ${new Date(data.generated_at).toLocaleTimeString()}` : ""}
            </p>
          </div>
          <div className="flex gap-1 rounded-md border border-border p-1">
            {WINDOWS.map((w) => (
              <button
                key={w.minutes}
                onClick={() => setWindowMinutes(w.minutes)}
                className={`rounded px-3 py-1 text-sm ${
                  windowMinutes === w.minutes
                    ? "bg-accent text-accent-foreground"
                    : "text-secondary hover:text-primary"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>

        {isLoading || !data ? (
          <>
            <CardSkeleton />
            <CardSkeleton />
          </>
        ) : (
          <>
            {/* Cloudflare: volume + sustained 4xx + 5xx + latency */}
            <section className="rounded-lg border border-border bg-surface p-5 space-y-4">
              <div className="flex items-center gap-2">
                <BoltIcon className="h-5 w-5 text-secondary" />
                <h2 className="text-lg font-semibold text-primary">Cloudflare (requests)</h2>
              </div>

              {!data.cloudflare.ok ? (
                <Unavailable what="Cloudflare metrics" error={data.cloudflare.error} />
              ) : (
                <p className="text-sm text-secondary">
                  Requests across top routes:{" "}
                  <span className="font-semibold text-primary">
                    {fmt(data.cloudflare.total_requests)}
                  </span>
                </p>
              )}

              {/* Sustained 4xx: routes failing for ~every caller over real volume. The backend
                  applies the rate + volume floors, so an empty list is genuinely quiet rather
                  than unfiltered noise (#1467).

                  Rendered OUTSIDE the `cloudflare.ok` gate: that flag covers only the 5xx + p95
                  queries, so nesting this under it let an unrelated query failure hide a live
                  4xx incident. `client_errors_ok` is this query's own flag, in both directions.
                  (Codex review, #1471.) */}
              <div>
                <h3 className="mb-2 text-sm font-medium text-secondary">
                  Sustained 4xx routes
                </h3>
                {data.cloudflare.client_errors_ok === false ? (
                  <Unavailable what="Sustained-4xx check" />
                ) : (data.cloudflare.top_client_errors ?? []).length === 0 ? (
                  <AllClear label="No sustained 4xx routes in window" />
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-secondary">
                        {/* "Endpoint", not "Route": these rows are keyed by (method, route),
                            since one pattern can serve several verbs (#1467). */}
                        <th className="py-1 pr-4 font-medium">Endpoint</th>
                        <th className="py-1 pr-4 font-medium">4xx</th>
                        <th className="py-1 pr-4 font-medium">Requests</th>
                        <th className="py-1 font-medium">Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.cloudflare.top_client_errors.map((e) => (
                        <tr
                          key={`${e.method} ${e.route}`}
                          className="border-t border-border/50"
                        >
                          <td className="py-1 pr-4 font-mono text-primary">
                            <span className="text-secondary">{e.method}</span> {e.route}
                          </td>
                          <td className="py-1 pr-4 text-amber-400">{fmt(e.errors)}</td>
                          <td className="py-1 pr-4 text-secondary">{fmt(e.requests)}</td>
                          <td className="py-1 text-primary">
                            {(Number(e.error_rate) * 100).toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* 5xx + p95 stay behind `cloudflare.ok` — those two queries are exactly
                  what that flag reports on. */}
              {data.cloudflare.ok && (
                <>
                  <div>
                    <h3 className="mb-2 text-sm font-medium text-secondary">Top 5xx routes</h3>
                    {data.cloudflare.top_errors.length === 0 ? (
                      <AllClear label="No 5xx errors in window" />
                    ) : (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-secondary">
                            <th className="py-1 pr-4 font-medium">Route</th>
                            <th className="py-1 pr-4 font-medium">5xx</th>
                            <th className="py-1 pr-4 font-medium">Requests</th>
                            <th className="py-1 font-medium">Rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.cloudflare.top_errors.map((e) => (
                            <tr key={e.route} className="border-t border-border/50">
                              <td className="py-1 pr-4 font-mono text-primary">{e.route}</td>
                              <td className="py-1 pr-4 text-rose-400">{fmt(e.errors)}</td>
                              <td className="py-1 pr-4 text-secondary">{fmt(e.requests)}</td>
                              <td className="py-1 text-primary">
                                {(Number(e.error_rate) * 100).toFixed(1)}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {data.cloudflare.slowest_routes.length > 0 && (
                    <div>
                      <h3 className="mb-2 flex items-center gap-1 text-sm font-medium text-secondary">
                        <ClockIcon className="h-4 w-4" /> Slowest routes (p95)
                      </h3>
                      <table className="w-full text-sm">
                        <tbody>
                          {data.cloudflare.slowest_routes.slice(0, 5).map((s) => (
                            <tr key={s.route} className="border-t border-border/50">
                              <td className="py-1 pr-4 font-mono text-primary">{s.route}</td>
                              <td className="py-1 text-primary">{fmt(s.p95_ms)}ms</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </section>

            {/* PostHog: JS exception groups */}
            <section className="rounded-lg border border-border bg-surface p-5 space-y-4">
              <div className="flex items-center gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-secondary" />
                <h2 className="text-lg font-semibold text-primary">JS exceptions (PostHog)</h2>
              </div>

              {!data.exceptions.ok ? (
                <Unavailable what="PostHog exceptions" error={data.exceptions.error} />
              ) : data.exceptions.groups.length === 0 ? (
                <AllClear label="No JS exceptions in window" />
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-secondary">
                      <th className="py-1 pr-4 font-medium">Type</th>
                      <th className="py-1 pr-4 font-medium">Message</th>
                      <th className="py-1 pr-4 font-medium">Count</th>
                      <th className="py-1 font-medium">Users</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.exceptions.groups.map((g, i) => (
                      <tr key={g.issue_id || i} className="border-t border-border/50 align-top">
                        <td className="py-1 pr-4 font-mono text-primary">{g.type || "Error"}</td>
                        <td className="py-1 pr-4 text-secondary">{g.message}</td>
                        <td className="py-1 pr-4 text-primary">{fmt(g.occurrences)}</td>
                        <td className="py-1 text-primary">{fmt(g.users)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </>
        )}
      </div>
    </Layout>
  )
}
